import { useEffect, useState } from "react";
import TopNavigator from "../components/TopNavigator";
import Chart from "../components/Chart";
import Prices from "../components/Prices";
import TredeDetail from "../components/TredeDetail";
import ContentTabs from "../pages/ContentTabs";
import Footer from "../components/Footer";
import { apiUrl } from "../config/api";
import { fetchUserPortalDetails } from "../utils/userPortalApi";

function getStoredUser() {
  try {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    return null;
  }
}

function MainPage() {
  const SEEN_MESSAGE_STORAGE_KEY = "main_page_seen_message_id";
  const [selectedItem, setSelectedItem] = useState(null);
  const [wallet, setWallet] = useState(() => getStoredUser());
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState("");
  const [showBalances, setShowBalances] = useState(true);
  const [latestMessageModal, setLatestMessageModal] = useState(null);
  const isLoggedIn = Boolean(localStorage.getItem("token") && localStorage.getItem("user"));

  useEffect(() => {
    const loadWallet = async () => {
      if (!isLoggedIn) {
        setWallet(null);
        setWalletLoading(false);
        return;
      }

      try {
        const data = await fetchUserPortalDetails();
        setWallet((prev) => ({
          ...prev,
          ...(data.user || {}),
        }));
        setWalletError("");
      } catch (error) {
        setWallet((prev) => prev || getStoredUser());
        setWalletError(error.message || "خطا در دریافت اطلاعات کیف پول");
      } finally {
        setWalletLoading(false);
      }
    };

    loadWallet();
  }, [isLoggedIn]);

  useEffect(() => {
    let isMounted = true;

    const syncLatestMessage = async () => {
      try {
        const response = await fetch(apiUrl("/api/content/daily-messages"));
        if (!response.ok) return;

        const data = await response.json();
        const latestMessage = Array.isArray(data) && data.length > 0 ? data[0] : null;
        if (!latestMessage || !isMounted) return;

        const latestMessageId = String(latestMessage.id);
        const seenMessageId = localStorage.getItem(SEEN_MESSAGE_STORAGE_KEY);

        if (!seenMessageId) {
          localStorage.setItem(SEEN_MESSAGE_STORAGE_KEY, latestMessageId);
          return;
        }

        if (seenMessageId !== latestMessageId) {
          setLatestMessageModal(latestMessage);
        }
      } catch (error) {
        // Silent fail: message polling should not block the page.
      }
    };

    syncLatestMessage();
    const intervalId = setInterval(syncLatestMessage, 15000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [SEEN_MESSAGE_STORAGE_KEY]);

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const formatAmount = (value, maximumFractionDigits = 2) =>
    Number(value || 0).toLocaleString("fa-IR", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    });

  const renderBalanceValue = (value, unit, maximumFractionDigits = 2) => {
    if (walletLoading && value == null) return "در حال دریافت...";
    if (!showBalances) return "••••••";
    if (value == null || value === "") return "----";

    return `${formatAmount(value, maximumFractionDigits)} ${unit}`;
  };

  const fullName = [wallet?.firstName, wallet?.lastName].filter(Boolean).join(" ");
  const displayName = fullName || wallet?.phone || wallet?.identifier || "----";
  const closeLatestMessageModal = () => {
    if (latestMessageModal?.id != null) {
      localStorage.setItem(SEEN_MESSAGE_STORAGE_KEY, String(latestMessageModal.id));
    }
    setLatestMessageModal(null);
  };

  return (
    <div className="MainPageShell">
      <TopNavigator />

      {latestMessageModal && (
        <div className="MainPageMessageOverlay" onClick={closeLatestMessageModal}>
          <div className="MainPageMessageModal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="MainPageMessageClose"
              onClick={closeLatestMessageModal}
              aria-label="بستن پیام"
            >
              ×
            </button>
            <span className="MainPageMessageEyebrow">پیام جدید</span>
            <h2 className="MainPageMessageTitle">{latestMessageModal.title}</h2>
            <p className="MainPageMessageDate">
              {latestMessageModal.date ? new Date(latestMessageModal.date).toLocaleString("fa-IR") : ""}
            </p>
            <div className="MainPageMessageBody">{latestMessageModal.content}</div>
          </div>
        </div>
      )}

      <main className="container MainPageContent">
        <section className="MainPageHero">

          {isLoggedIn && (
          <div className="MainPageWallet">
            <div className="MainPageWalletHeader">
              <div>
                <span className="MainPageHighlightLabel">وضعیت کیف پول</span>
                <strong className="MainPageHighlightValue">
                  {walletLoading && displayName === "----" ? "در حال دریافت..." : displayName}
                </strong>
              </div>
              <button
                type="button"
                className="MainPageWalletToggle"
                onClick={() => setShowBalances((prev) => !prev)}
                aria-label={showBalances ? "مخفی کردن مانده‌ها" : "نمایش مانده‌ها"}
                title={showBalances ? "مخفی کردن مانده‌ها" : "نمایش مانده‌ها"}
              >
                {showBalances ? "👁" : "🙈"}
              </button>
            </div>

            <div className="MainPageWalletGrid">
              <div className="MainPageWalletCard">
                <span>مانده پولی</span>
                <strong>{renderBalanceValue(wallet?.moneyBalance, "تومان", 0)}</strong>
              </div>
              <div className="MainPageWalletCard">
                <span>مانده طلا</span>
                <strong>{renderBalanceValue(wallet?.goldBalance, "گرم")}</strong>
              </div>
            </div>

            {walletError && wallet?.moneyBalance == null && wallet?.goldBalance == null && (
              <p className="MainPageWalletMessage">{walletError}</p>
            )}
          </div>
          )}
        </section>

        <section className="MainPageGrid">
          <div className="MainPagePanel MainPagePanelPrices">
            <div className="MainPagePanelHeader">
              <div>
                <span className="MainPagePanelEyebrow">تابلو</span>
                <h3 className="MainPagePanelTitle">قیمت‌های قابل معامله</h3>
              </div>
            </div>
            <Prices onItemSelect={handleItemSelect} />
          </div>

          <div className="MainPagePanel MainPagePanelChart">
            <div className="MainPagePanelHeader">
              <div>
                <span className="MainPagePanelEyebrow">نمودار</span>
                <h3 className="MainPagePanelTitle">روند لحظه‌ای بازار</h3>
              </div>
            </div>
            <Chart selectedItem={selectedItem} />
          </div>
        </section>

        {isLoggedIn && <TredeDetail />}

        <section className="MainPageTabsSection">
          <div className="MainPageSectionHeading">
            <span className="MainPagePanelEyebrow">ویترین</span>
            <h3 className="MainPagePanelTitle">گالری و پیام‌ها</h3>
          </div>
          <ContentTabs />
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default MainPage;
